import React, { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  MapPin, Route, IndianRupee, Calendar, Check, X, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, Map, Navigation
} from "lucide-react";
import { toast } from "sonner";

export default function TravelPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("my-travel");
  const [loading, setLoading] = useState(false);
  const [todayTravel, setTodayTravel] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [circleSummary, setCircleSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedLog, setSelectedLog] = useState(null);

  const isFieldStaff = ["technician", "helper", "surveyor", "supervisor"].includes(user?.role);
  const isReviewer = ["supervisor", "ci", "admin", "package_head"].includes(user?.role);

  const fetchTodayTravel = useCallback(async () => {
    if (!isFieldStaff) return;
    try {
      const res = await api.get("/travel/my-today");
      setTodayTravel(res.data);
    } catch (err) {
      console.error("Error fetching today:", err);
    }
  }, [isFieldStaff]);

  const fetchHistory = useCallback(async () => {
    if (!isFieldStaff) return;
    try {
      const res = await api.get(`/travel/my-history?month=${selectedMonth}`);
      setHistoryData(res.data);
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  }, [selectedMonth, isFieldStaff]);

  const fetchReviewQueue = useCallback(async () => {
    if (!isReviewer) return;
    try {
      const res = await api.get("/travel/review-queue");
      setReviewQueue(res.data.items || []);
    } catch (err) {
      console.error("Error fetching review queue:", err);
    }
  }, [isReviewer]);

  const fetchCircleSummary = useCallback(async () => {
    if (!isReviewer) return;
    try {
      const res = await api.get(`/travel/circle-summary?month=${selectedMonth}`);
      setCircleSummary(res.data);
    } catch (err) {
      console.error("Error fetching circle summary:", err);
    }
  }, [isReviewer, selectedMonth]);

  useEffect(() => {
    fetchTodayTravel();
    fetchHistory();
    fetchReviewQueue();
    fetchCircleSummary();
  }, [fetchTodayTravel, fetchHistory, fetchReviewQueue, fetchCircleSummary]);

  const handleApprove = async (logId) => {
    try {
      await api.post(`/travel/approve/${logId}`);
      toast.success("TA approved");
      fetchReviewQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const handleReject = async (logId) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    try {
      await api.post(`/travel/reject/${logId}`, { reason });
      toast.success("TA rejected");
      fetchReviewQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const handleAdjust = async (logId) => {
    const km = prompt("Adjusted KM:");
    if (!km) return;
    const reason = prompt("Reason for adjustment:");
    try {
      await api.post(`/travel/approve/${logId}`, { 
        adjusted_km: parseFloat(km), 
        adjustment_reason: reason 
      });
      toast.success("TA adjusted and approved");
      fetchReviewQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to adjust");
    }
  };

  const viewLogDetail = async (logId) => {
    try {
      const res = await api.get(`/travel/detail/${logId}`);
      setSelectedLog(res.data);
    } catch (err) {
      toast.error("Failed to load details");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "bg-green-100 text-green-700";
      case "adjusted": return "bg-blue-100 text-blue-700";
      case "rejected": return "bg-red-100 text-red-700";
      case "calculated": return "bg-yellow-100 text-yellow-700";
      case "pending_review": return "bg-orange-100 text-orange-700";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  // Non-field staff see a different view
  if (!isFieldStaff && !isReviewer) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <Route className="w-12 h-12 mb-4" />
        <p>Travel Allowance is applicable for field staff only</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="travel-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Travel Allowance</h1>
          <p className="text-sm text-slate-500">GPS Route Tracking & TA Claims</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-slate-600">Rate: ₹3.00/km</div>
          <div className="text-xs text-slate-400">Road Factor: 1.3×</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {isFieldStaff && (
          <>
            <button
              onClick={() => setActiveTab("my-travel")}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "my-travel" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              <MapPin className="inline-block w-4 h-4 mr-1" />
              Today's Travel
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "history" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              <Calendar className="inline-block w-4 h-4 mr-1" />
              My History
            </button>
          </>
        )}
        {isReviewer && (
          <>
            <button
              onClick={() => setActiveTab("review")}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "review" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              <Check className="inline-block w-4 h-4 mr-1" />
              Review Queue
              {reviewQueue.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{reviewQueue.length}</span>}
            </button>
            <button
              onClick={() => setActiveTab("summary")}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "summary" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              <IndianRupee className="inline-block w-4 h-4 mr-1" />
              Circle Summary
            </button>
          </>
        )}
      </div>

      {/* Today's Travel Tab */}
      {activeTab === "my-travel" && isFieldStaff && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Route Summary Card */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Route className="w-5 h-5 text-orange-500" />
              Today's Route
            </h3>
            {todayTravel ? (
              todayTravel.error ? (
                <div className="text-center py-8 text-slate-400">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                  <p>{todayTravel.error}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">{todayTravel.total_road_km?.toFixed(1) || 0}</div>
                      <div className="text-xs text-blue-700">Total KM</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">₹{todayTravel.ta_amount?.toFixed(2) || 0}</div>
                      <div className="text-xs text-green-700">TA Amount</div>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-600">{todayTravel.dts_visited || 0}</div>
                      <div className="text-xs text-purple-700">DTs Visited</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-600">{todayTravel.total_segments || 0}</div>
                      <div className="text-xs text-orange-700">Segments</div>
                    </div>
                  </div>
                  
                  {/* Origin & Return */}
                  <div className="text-sm space-y-2">
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium">Origin</span>
                      {todayTravel.origin_name} ({todayTravel.origin_type})
                      <span className="text-slate-400">→ {todayTravel.origin_to_first_dt_km?.toFixed(1)}km</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium">Return</span>
                      {todayTravel.return_name} ({todayTravel.return_type})
                      <span className="text-slate-400">→ {todayTravel.last_dt_to_return_km?.toFixed(1)}km</span>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
                <p>Loading travel data...</p>
              </div>
            )}
          </div>

          {/* GPS Points List */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Navigation className="w-5 h-5 text-orange-500" />
              Route Points
            </h3>
            {todayTravel?.gps_points?.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {todayTravel.gps_points.map((point, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      point.type === "punch_in" ? "bg-green-500 text-white" :
                      point.type === "punch_out" ? "bg-red-500 text-white" :
                      "bg-blue-500 text-white"
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{point.name}</div>
                      <div className="text-xs text-slate-400">
                        {point.lat?.toFixed(4)}, {point.lng?.toFixed(4)}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500">
                      {point.timestamp ? new Date(point.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <MapPin className="w-8 h-8 mx-auto mb-2" />
                <p>No GPS points recorded yet</p>
                <p className="text-xs mt-1">Submit DT forms to record route</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && isFieldStaff && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Travel History</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => {
                const d = new Date(selectedMonth + "-01");
                d.setMonth(d.getMonth() - 1);
                setSelectedMonth(d.toISOString().slice(0, 7));
              }} className="p-1 hover:bg-slate-100 rounded">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-1 border border-slate-200 rounded text-sm"
              />
              <button onClick={() => {
                const d = new Date(selectedMonth + "-01");
                d.setMonth(d.getMonth() + 1);
                setSelectedMonth(d.toISOString().slice(0, 7));
              }} className="p-1 hover:bg-slate-100 rounded">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Summary */}
          {historyData?.summary && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <div className="text-xl font-bold text-blue-600">{historyData.summary.total_km?.toFixed(1)}</div>
                <div className="text-xs text-blue-700">Total KM</div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <div className="text-xl font-bold text-green-600">₹{historyData.summary.total_ta?.toFixed(0)}</div>
                <div className="text-xs text-green-700">Total TA</div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <div className="text-xl font-bold text-purple-600">{historyData.summary.days_claimed}</div>
                <div className="text-xs text-purple-700">Days Claimed</div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg text-center">
                <div className="text-xl font-bold text-yellow-600">{historyData.summary.pending_review}</div>
                <div className="text-xs text-yellow-700">Pending</div>
              </div>
            </div>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3">Date</th>
                <th className="text-left py-2 px-3">KM</th>
                <th className="text-left py-2 px-3">TA Amount</th>
                <th className="text-left py-2 px-3">DTs</th>
                <th className="text-left py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {historyData?.logs?.length ? historyData.logs.map((log, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => viewLogDetail(log.id)}>
                  <td className="py-2 px-3 font-medium">{log.log_date}</td>
                  <td className="py-2 px-3">{log.approved_km?.toFixed(1) || log.total_road_km?.toFixed(1)}</td>
                  <td className="py-2 px-3">₹{log.ta_amount?.toFixed(2)}</td>
                  <td className="py-2 px-3">{log.dts_visited}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${getStatusColor(log.status)}`}>{log.status}</span>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">No travel records for this month</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Review Queue Tab */}
      {activeTab === "review" && isReviewer && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4">TA Review Queue (Yesterday)</h3>
          {reviewQueue.length > 0 ? (
            <div className="space-y-4">
              {reviewQueue.map((log) => (
                <div key={log.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-medium text-slate-800">{log.employee_name}</div>
                      <div className="text-sm text-slate-500">{log.log_date}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-600">{log.total_road_km?.toFixed(1)} km</div>
                      <div className="text-sm text-green-600">₹{log.ta_amount?.toFixed(2)}</div>
                    </div>
                  </div>
                  
                  {/* Flags */}
                  {log.flags?.length > 0 && (
                    <div className="flex gap-2 mb-3">
                      {log.flags.map((flag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
                          ⚠️ {flag.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-2 mb-3 text-xs">
                    <div className="p-2 bg-white rounded text-center">
                      <div className="font-bold">{log.dts_visited}</div>
                      <div className="text-slate-500">DTs</div>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <div className="font-bold">{log.total_segments}</div>
                      <div className="text-slate-500">Segments</div>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <div className="font-bold">{log.origin_type}</div>
                      <div className="text-slate-500">Origin</div>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <div className="font-bold">{log.return_type}</div>
                      <div className="text-slate-500">Return</div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(log.id)}
                      className="px-4 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 flex items-center gap-1"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleAdjust(log.id)}
                      className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                    >
                      Adjust KM
                    </button>
                    <button
                      onClick={() => handleReject(log.id)}
                      className="px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Reject
                    </button>
                    <button
                      onClick={() => viewLogDetail(log.id)}
                      className="px-4 py-2 bg-slate-500 text-white text-sm rounded hover:bg-slate-600 flex items-center gap-1"
                    >
                      <Map className="w-4 h-4" /> View Route
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Check className="w-12 h-12 mx-auto mb-2" />
              <p>No pending TA reviews 🎉</p>
            </div>
          )}
        </div>
      )}

      {/* Circle Summary Tab */}
      {activeTab === "summary" && isReviewer && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Circle TA Summary</h3>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-1 border border-slate-200 rounded text-sm"
            />
          </div>
          
          {circleSummary ? (
            <>
              {/* Totals */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{circleSummary.total_km?.toFixed(0)}</div>
                  <div className="text-xs text-blue-700">Total KM</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">₹{circleSummary.total_ta?.toFixed(0)}</div>
                  <div className="text-xs text-green-700">Total TA</div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-600">{circleSummary.total_claims}</div>
                  <div className="text-xs text-purple-700">Claims</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-orange-600">{circleSummary.avg_km_per_day?.toFixed(1)}</div>
                  <div className="text-xs text-orange-700">Avg KM/Day</div>
                </div>
              </div>
              
              {/* By Employee */}
              <h4 className="font-medium text-slate-700 mb-3">By Employee</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3">Employee</th>
                    <th className="text-right py-2 px-3">Days</th>
                    <th className="text-right py-2 px-3">Total KM</th>
                    <th className="text-right py-2 px-3">Total TA</th>
                  </tr>
                </thead>
                <tbody>
                  {circleSummary.by_employee?.map((emp, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium">{emp.employee_name}</td>
                      <td className="py-2 px-3 text-right">{emp.days_claimed}</td>
                      <td className="py-2 px-3 text-right">{emp.total_km?.toFixed(1)}</td>
                      <td className="py-2 px-3 text-right text-green-600">₹{emp.total_ta?.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          )}
        </div>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-semibold text-slate-800">Travel Details - {selectedLog.log_date}</h3>
              <button onClick={() => setSelectedLog(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <div className="text-xl font-bold text-blue-600">{selectedLog.total_road_km?.toFixed(1)}</div>
                  <div className="text-xs text-blue-700">Total KM</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <div className="text-xl font-bold text-green-600">₹{selectedLog.ta_amount?.toFixed(2)}</div>
                  <div className="text-xs text-green-700">TA Amount</div>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <div className="text-xl font-bold text-purple-600">{selectedLog.dts_visited}</div>
                  <div className="text-xs text-purple-700">DTs Visited</div>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <span className={`px-2 py-1 text-xs font-bold rounded uppercase ${getStatusColor(selectedLog.status)}`}>{selectedLog.status}</span>
                </div>
              </div>
              
              {/* Origin & Return */}
              <div className="text-sm space-y-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-slate-600">Origin:</span>
                  <span className="font-medium">{selectedLog.origin_name} ({selectedLog.origin_type}) - {selectedLog.origin_to_first_dt_km?.toFixed(1)}km</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Return:</span>
                  <span className="font-medium">{selectedLog.return_name} ({selectedLog.return_type}) - {selectedLog.last_dt_to_return_km?.toFixed(1)}km</span>
                </div>
              </div>
              
              {/* Segments */}
              <h4 className="font-medium text-slate-700">Segments</h4>
              <div className="space-y-2">
                {selectedLog.segments?.map((seg, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg text-sm">
                    <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {seg.segment_order}
                    </div>
                    <div className="flex-1">
                      <div>{seg.from_name} → {seg.to_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{seg.road_km?.toFixed(2)} km</div>
                      {seg.is_deviation && <span className="text-xs text-amber-500">⚠️ Deviation</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
